/**
 * Composant de prédiction solaire - SolarPredict
 * 
 * Page principale permettant de lancer une prédiction en une étape :
 * - Étape 1 : Upload des données environnementales (name="file")
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import FileUploader from '../components/ui/FileUploader';
import AlertMessage from '../components/ui/AlertMessage';
import { uploadSensorData, setPredictionConfig, updateChosenString } from '../api/prediction';
import { ChartDataProvider, useChartData } from '../context/ChartDataContext';
import { usePredictState } from '../context/PredictStateContext';
import ApexChart from './charts/ApexChart';
import ApexChart2 from './charts/ApexChart2';
import ApexChart3 from './charts/ApexChart3';
import ApexChart4 from './charts/ApexChart4';
import ApexChart5 from './charts/ApexChart5';
import ApexChart6 from './charts/ApexChart6';
import POAChart from './charts/POA_chart';
const Predict = () => {
  return (
    <ChartDataProvider>
      <PredictContent />
    </ChartDataProvider>
  );
};

const PredictContent = () => {
  // Use persistent state instead of local state
  const {
    file, setFile,
    sensorDataId, setSensorDataId,
    isUploading, setIsUploading,
    uploadSuccess, setUploadSuccess,
    uploadError, setUploadError,
    showChart, setShowChart,
    selectedString, setSelectedString,
    selectedModel, setSelectedModel,
    clearState
  } = usePredictState();
  
  const { fetchChartData, clearChartData, hasCachedData, chartData, isDataFetched } = useChartData();

  // Debug: Log state on mount
  useEffect(() => {
    console.log('PredictContent mounted with state:', {
      uploadSuccess,
      sensorDataId,
      showChart,
      isDataFetched,
      hasChartData: !!chartData
    });
  }, [uploadSuccess, sensorDataId, showChart, isDataFetched, chartData]);

  const { isAuthenticated } = useAuth(); 
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/predict' } } });
    }
  }, [isAuthenticated, navigate]);

  // Auto-show charts when cached data is available and upload was successful
  useEffect(() => {
    console.log('Auto-show effect triggered:', {
      uploadSuccess,
      sensorDataId,
      isDataFetched,
      hasChartData: !!chartData,
      showChart
    });
    
    // Only auto-show if all conditions are met and charts are not already showing
    if (uploadSuccess && sensorDataId && isDataFetched && chartData && !showChart) {
      console.log('Auto-showing charts - cached data available and upload successful');
      // Use a timeout to prevent rapid state changes
      setTimeout(() => {
        setShowChart(true);
      }, 100);
    }
  }, [uploadSuccess, sensorDataId, isDataFetched, chartData, showChart, setShowChart]);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setUploadSuccess(false);
    setSensorDataId(null);
    setUploadError('');
    setShowChart(false); // Hide charts when new file is selected
    
    // Clear chart data cache when new file is selected
    clearChartData();
  };

  const handleFileUpload = async () => {
    if (!file) {
      setUploadError('Please select an XLSX file containing your environmental data');
      return;
    }
    setIsUploading(true);
    setUploadError('');
    
    // FORCE clear all caches and states before uploading new file
    console.log('🧹 Clearing all caches before new upload...');
    clearChartData();
    setShowChart(false);
    
    try {
      const response = await uploadSensorData(file);
      if (response.success) {
        setUploadSuccess(true);
        setSensorDataId(response.data.id);
        console.log('✅ New file uploaded with ID:', response.data.id);
      } else {
        setUploadError(response.message || 'Une erreur est survenue lors de l\'upload');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      setUploadError(error.message || 'Une erreur inconnue est survenue');
    } finally {
      setIsUploading(false);
    }
  };

  const handleShowChart = async () => {
    try {
      // Always clear cache and fetch fresh data for new predictions
      console.log('Clearing cache and fetching fresh prediction data...');
      clearChartData();
      
      // Send configuration to backend before running prediction
      console.log('Sending configuration:', { stringId: selectedString, modelId: selectedModel });
      
      const configResponse = await setPredictionConfig(selectedString, selectedModel);
      console.log('Configuration set successfully:', configResponse);
      
      // Fetch chart data once for all charts (fresh data, not cached)
      console.log('Fetching chart data for all charts...');
      await fetchChartData();
      
      // Now show charts (they will use the shared data)
      setShowChart(true);
    } catch (error) {
      console.error('Error setting configuration:', error);
      setUploadError('Failed to set prediction configuration. Please try again.');
    }
  };

  const handleStringChange = async (event) => {
    const newString = event.target.value;
    setSelectedString(newString);
    console.log('Selected string:', newString);
    
    // Convert string selection to numeric ID and update database
    const stringId = parseInt(newString.replace('string', ''));
    console.log('Converting to stringId:', stringId);
    
    try {
      console.log('Calling API to update string...');
      const response = await updateChosenString(stringId);
      console.log('API response:', response);
      if (response.success) {
        console.log('✅ String updated successfully in database');
      }
    } catch (error) {
      console.error('❌ Failed to update string:', error);
    }
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
    console.log('Selected model:', event.target.value);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl predict-page">
      {/* Enhanced Main Title */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full shadow-2xl mb-6">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        
        <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-green-600 to-blue-800 bg-clip-text text-transparent mb-4">
          Performance Prediction
        </h1>
        
        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-green-500 mx-auto rounded-full mb-6"></div>
        
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Optimize your photovoltaic installations with our advanced predictive models. 
          <span className="font-semibold text-blue-600"> Select your string and model</span> and 
          <span className="font-semibold text-green-600"> upload your data</span> for accurate predictions.
        </p>
        
        {/* Feature highlights */}

      </div>
      
      {/* String Selection and File Upload in One Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* String & Model Selection Panel - Left Column */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 px-6 py-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Configuration Selection</h3>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <div className="max-w-md mx-auto space-y-6">
              {/* String Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
                  🔌 String Configuration
                </label>
                <div className="relative">
                  <select 
                    className="w-full px-6 py-4 text-lg font-semibold text-gray-800 bg-gray-50 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 hover:bg-white appearance-none cursor-pointer"
                    value={selectedString}
                    onChange={handleStringChange}
                  >
                    <option value="string1">⚡ String 1 - Jinko Cheetah</option>
                    <option value="string2">⚡ String 2 - Jinko Tiger Pro</option>
                    <option value="string3">⚡ String 3 - Jinko Eagle</option>
                  </select>
                  
                  {/* Custom dropdown arrow */}
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
                  🧠 Prediction Model
                </label>
                <div className="relative">
                  <select 
                    className="w-full px-6 py-4 text-lg font-semibold text-gray-800 bg-gray-50 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 hover:border-green-300 hover:bg-white appearance-none cursor-pointer"
                    value={selectedModel}
                    onChange={handleModelChange}
                  >
                    <option value="model1">🔬 Model 1 - DM model</option>
                    <option value="model2">🔬 Model 2 - PLM model</option>
                    <option value="model3">🔬 Model 3 - BM model</option>
                  </select>
                  
                  {/* Custom dropdown arrow */}
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Selection display */}
              <div className="mt-6 text-center space-y-3">
                <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-green-50 px-6 py-3 rounded-full border border-blue-200">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-700">
                    String: <span className="font-bold text-blue-600">{selectedString}</span>
                  </span>
                </div>
                <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-50 to-blue-50 px-6 py-3 rounded-full border border-green-200">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-700">
                    Model: <span className="font-bold text-green-600">{selectedModel}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Panel - Right Column */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Data Upload</h3>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <div className="max-w-md mx-auto">
              <div className="flex items-center justify-center mb-4">
                <p className="text-gray-600 text-center">
                  Upload an XLSX file containing environmental data.
                </p>
                {/* Help icon with tooltip */}
                <div className="relative ml-2 group">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center cursor-help hover:bg-blue-200 transition-colors">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {/* Tooltip with image */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
                    <div className="bg-white border border-gray-300 rounded-xl shadow-2xl p-4 max-w-[90vw] overflow-auto">
                      <img 
                        src="/images/proto.png" 
                        alt="File upload format example" 
                        className="w-[1200px] max-w-full h-auto rounded-lg border border-gray-200 shadow-md"
                        style={{minWidth: '424px', minHeight: '134px'}}
                        onLoad={() => console.log('Image loaded successfully')}
                        onError={() => console.log('Image failed to load')}
                      />
                      <div className="text-sm text-gray-600 mt-3 text-center font-medium bg-gray-50 py-2 px-4 rounded-lg">
                        📋 Expected file format: XLSX file with columns for timestamp, temperature (°C), and irradiance (W/m²)
                      </div>
                    </div>
                    {/* Arrow pointing up */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-b-6 border-transparent border-b-white"></div>
                  </div>
                </div>
              </div>
              <FileUploader
                onFileSelect={handleFileSelect}
                accept=".xlsx"
                label={uploadSuccess ? "File uploaded successfully" : "Select XLSX file"}
                disabled={uploadSuccess}
              />
              <div className="mt-2 text-sm text-gray-500 text-center">
                📄 Required format: XLSX file with timestamp, temperature (°C), and irradiance (W/m²) columns
              </div>
              {uploadError && (
                <AlertMessage
                  type="error"
                  message={uploadError}
                  onClose={() => setUploadError('')}
                  className="mt-3"
                />
              )}
              <div className="mt-6">
                <Button
                  onClick={handleFileUpload}
                  isLoading={isUploading}
                  disabled={!file || isUploading}
                  type="primary"
                  className="w-full"
                >
                  Upload File
                </Button>
              </div>
              {uploadSuccess && (
                <div className="mt-3 space-y-3">
                  <AlertMessage
                    type="success"
                    message="Data uploaded successfully!"
                  />
                  {/* Hidden file input for selecting new file */}
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (selectedFile) {
                        // Reset states and set new file
                        setUploadSuccess(false);
                        setSensorDataId(null);
                        setUploadError('');
                        setShowChart(false);
                        clearChartData();
                        setFile(selectedFile);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="newFileInput"
                  />
                  <Button
                    onClick={() => {
                      // Trigger the file input click
                      document.getElementById('newFileInput').click();
                    }}
                    type="primary"
                    className="w-full"
                  >
                    📁 Select New File
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Card title="Launch Prediction" className="bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex flex-col items-center justify-center">
          <div className="mb-4 text-center max-w-xl">
            <p className="text-gray-700">
              After uploading your environmental data, click the button to display the prediction graph.
            </p>
          </div>
          <Button
            onClick={handleShowChart}
            disabled={!sensorDataId}
            type="primary"
            size="lg"
            className="mb-6"
          >
            Launch Prediction
          </Button>
        </div>
      </Card>

      {/* Chart Display Section */}
      {showChart && (
        <Card title="Prediction Results" className="mt-6">
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              Real-time prediction data visualization.
            </p>
          </div>
          
          
          {/* Charts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Chart 6 - T_K */}
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Temperature (°C)</h3>
              <ApexChart6 />
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Irradiance (W/m²)</h3>
              <POAChart />
            </div>
            {/* Chart 1 - ISC */}
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Predicted short circuit current (I<sub className="italic lowercase">sc</sub>)</h3>
              <ApexChart />
            </div>
            {/* Chart 2 - VOC */}
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Predicted open circuit voltage (V<sub className="italic lowercase">oc</sub>)</h3>
              <ApexChart2 />
            </div>
            
            {/* Chart 3 - VMPP */}
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Predicted maximum power point voltage (V<sub className="italic lowercase">mpp</sub>)</h3>
              <ApexChart3 />
            </div>
            
            {/* Chart 4 - IMPP */}
            <div className="bg-white rounded-lg p-4 border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Predicted maximum power point current (I<sub className="italic lowercase">mpp</sub>)</h3>
              <ApexChart4 />
            </div>
            
            {/* Chart 5 - MPP - Wide Chart */}
            <div className="bg-white rounded-lg p-4 border md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Predicted maximum power point (P<sub className="italic lowercase">mpp</sub>)
			  </h3>
              <ApexChart5 />
            </div>
            
          </div>
        </Card>
      )}
    </div>
  );
};

export default Predict;